import {SocketMatchmakingSend} from '../socket/SocketMatchmakingSend.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas.ts'
import {CA} from '../analytics/Analytics.ts'
import {createDivIn, createSpanIn} from '../ui_utils/DomUtils.ts'
import {type TimeoutId} from '@colonist/types'

type InitPowerInstream = (id: string) => void

declare const window: {
    vitag?: {
        instreamConfig?: {
            loadingText: string
            hidden: () => void
            complete: () => void
            error: () => void
            started: () => void
        }
    }
    viAPItag?: {
        initPowerInstream?: InitPowerInstream
    }
}

export class UIVLIController {
    private loadingAdBackground: HTMLDivElement | undefined
    private instreamAdStartTimeout: TimeoutId | undefined
    private readonly INSTREAM_AD_START_TIMEOUT = 7500

    constructor() {
        if(window.vitag == undefined) return

        window.vitag.instreamConfig = {
            loadingText: 'Loading advertisement..',
            hidden: () => this.userSkippedAd(),
            complete: () => this.userFinishedWatching(),
            error: () => this.adFailedToStart(),
            started: () => this.adIsReadyToBeShown(),
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private userSkippedAd() {
        // eslint-disable-next-line no-console
        console.log(`[Preroll] Ad Hidden/Skipped`)
        UIVLIController.sendAdsPerformanceData(AnalyticEventType.VideoAdsComplete)
    }

    private static sendMatchmakingRequestAfterAd() {
        if(!SocketMatchmakingSend.lastMatchmakingRequest) return

        const shouldDelay = false
        SocketMatchmakingSend.enterMatchmaker(SocketMatchmakingSend.lastMatchmakingRequest, shouldDelay)
    }

    // eslint-disable-next-line class-methods-use-this
    private userFinishedWatching() {
        // eslint-disable-next-line no-console
        console.log(`[Preroll] User completed watching ad`)
        UIVLIController.sendMatchmakingRequestAfterAd()
    }

    private adFailedToStart() {
        // eslint-disable-next-line no-console
        console.log('[Preroll] Ad error occurred. Sending to matchmaking now')
        this.sendToMatchmakingBecauseAdFailed()
    }

    private adIsReadyToBeShown() {
        // eslint-disable-next-line no-console
        console.log('[Preroll] Ad started')
        this.removeLoadingAdBackground()

        clearTimeout(this.instreamAdStartTimeout)
        this.instreamAdStartTimeout = undefined
    }

    startMatchmakingVideoAd() {
        UIVLIController.sendAdsPerformanceData(AnalyticEventType.VideoAdsCalled)

        if(window.viAPItag?.initPowerInstream !== undefined) {
            this.startInstreamAd(window.viAPItag.initPowerInstream)
        } else {
            // Fallback if nothing works
            UIVLIController.sendMatchmakingRequestAfterAd()
            UIVLIController.sendAdsPerformanceData(AnalyticEventType.VideoAdsBlocked)
        }
    }

    private startInstreamAd(initPowerInstream: InitPowerInstream) {
        this.loadingAdBackground = UIVLIController.buildLoadingAdBackground(document.body)
        this.instreamAdStartTimeout = setTimeout(() => {
            // eslint-disable-next-line no-console
            console.log('[Preroll] Ad timeout occurred. Sending to matchmaking now')
            this.sendToMatchmakingBecauseAdFailed()
        }, this.INSTREAM_AD_START_TIMEOUT)

        initPowerInstream(UIUtilsEjsVariables.vliPrerollId)
    }

    private sendToMatchmakingBecauseAdFailed() {
        clearTimeout(this.INSTREAM_AD_START_TIMEOUT)
        this.instreamAdStartTimeout = undefined

        this.removeLoadingAdBackground()
        UIVLIController.removeVLIPrerollBecauseAdFailed()

        UIVLIController.sendMatchmakingRequestAfterAd()
        UIVLIController.sendAdsPerformanceData(AnalyticEventType.VideoAdsNeverLoaded)
    }

    static sendAdsPerformanceData(adsAnalyticEventType: AnalyticEventType) {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: adsAnalyticEventType,
            category: AnalyticEventCategory.AdsPerformance,
        }
        void CA.sendAnalyticEvent(eventData)
    }

    private static buildLoadingAdBackground(parentContainer: HTMLElement) {
        const overlay = createDivIn(parentContainer, 'game-reconnecting-overlay')
        createDivIn(overlay, 'loading-spinner')
        createSpanIn(overlay, 'game-reconnecting-text', {key: 'strings:roomPage.loadingAd'})
        return overlay
    }

    private removeLoadingAdBackground() {
        this.loadingAdBackground?.remove()
    }

    private static removeVLIPrerollBecauseAdFailed() {
        const vliPreroll = document.querySelector(`[data-ad-slot="${UIUtilsEjsVariables.vliPrerollId}"]`)
        vliPreroll?.remove()
    }
}
